import {
  BeakerIcon,
  CalculatorIcon,
  ChatBubbleLeftEllipsisIcon,
  EyeIcon,
  PlusIcon,
  TagIcon,
  TicketIcon,
  TruckIcon,
} from "@heroicons/react/20/solid";
import {type ContainerStatusEntity, restrictionType} from "@ots-frontend/ots-data";
import type {ReactNode} from "react";
import type {OtsToolAction, OtsToolGroup} from "../components/OtsToolsMenu";

/** The container actions a page can offer; the same IDs as ots-ui's ContainerAction, plus the menu's own. */
export type ContainerToolId =
  | "VIEW"
  | "QUICK_VIEW"
  | "CALCULATION_STEPS"
  | "DISPATCH"
  | "DISPATCH_SPLIT_PICKUP"
  | "DISPATCH_SPLIT_DELIVERY"
  | "DISPATCH_REQUEST"
  | "TOGGLE_DO_NOT_DISPATCH"
  | "TOGGLE_DNU"
  | "TOGGLE_DISPATCH_IGNORE_MAX_NON_PRIMARY_VOLUME"
  | "TOGGLE_LACT_METER_DNU"
  | "TOGGLE_REJECTION"
  | "TOGGLE_SITE_TRANSPORT_VIA_PIPELINE"
  | "TOGGLE_SITE_TRANSPORT_OIL_VIA_PIPELINE"
  | "MANAGE_TAGS";

export type ContainerToolPermissions = {
  /** dispatch_create */
  createDispatch: boolean;
  /** dispatchrequest_edit: requests, the container flags, pipelines, and tags. */
  editDispatchRequest: boolean;
};

export type ContainerToolOptions = {
  /** What the page offers; anything else is left out whatever the permissions. */
  actions: readonly string[];
  permissions: ContainerToolPermissions;
  /** Offers Quick View, which opens the container's details panel beside the grid. */
  quickView?: boolean;
  /** Replaces the default icon of an action, e.g. with the matching status column icon. */
  icons?: Partial<Record<ContainerToolId, ReactNode>>;
};

const hasRejection = (container: ContainerStatusEntity) =>
  container.restrictions?.some((restriction) => restriction.type === restrictionType.rejection) ?? false;

const hasCalculation = (container: ContainerStatusEntity) =>
  container.metadata?.measurements?.included !== false && !!container.meterFill?.availableLoadDecisionGraph?.nodes.length;

const icon = (Icon: typeof EyeIcon) => <Icon aria-hidden="true"/>;

/**
 * The right-click menu for one container or several, in Title Case like the other workspace menus. One container gets everything the page offers and the user
 * may do, with toggles worded for its current state; several get only what works on all of them at once.
 */
export const buildContainerToolGroups = (
  targets: readonly ContainerStatusEntity[],
  {actions, permissions, quickView = false, icons = {}}: ContainerToolOptions,
): OtsToolGroup[] => {
  const offered = new Set(actions);
  const action = (id: ContainerToolId, label: string, fallback: ReactNode, show = true): OtsToolAction | undefined =>
    show ? {id, label, icon: icons[id] ?? fallback} : undefined;
  const group = (id: string, label: string, items: (OtsToolAction | undefined)[]): OtsToolGroup =>
    ({id, label, actions: items.filter((item): item is OtsToolAction => !!item)});
  const {createDispatch, editDispatchRequest} = permissions;
  const edit = (id: ContainerToolId) => editDispatchRequest && offered.has(id);

  if (targets.length > 1) {
    const count = `${targets.length} Containers`;
    return [
      group("dispatch", "Dispatch", [
        // The single-container Dispatch becomes a split pickup when several are picked.
        action("DISPATCH_SPLIT_PICKUP", `New Split-Pickup Dispatch (${count})`, icon(TruckIcon),
          createDispatch && (offered.has("DISPATCH") || offered.has("DISPATCH_SPLIT_PICKUP"))),
        action("DISPATCH_SPLIT_DELIVERY", `New Split-Delivery Dispatch (${count})`, icon(TruckIcon),
          createDispatch && offered.has("DISPATCH_SPLIT_DELIVERY")),
      ]),
      group("tags", "Tags", [action("MANAGE_TAGS", `Manage Tags (${count})`, icon(TagIcon), edit("MANAGE_TAGS"))]),
    ].filter((item) => item.actions.length);
  }

  const [container] = targets;
  if (!container) return [];
  const lact = container.lactMeters?.length === 1 ? container.lactMeters[0] : undefined;

  return [
    group("view", "View", [
      action("VIEW", "View Container", icon(EyeIcon)),
      action("QUICK_VIEW", "Quick View", icon(TicketIcon), quickView),
      action("CALCULATION_STEPS", "View Calculation Steps", icon(CalculatorIcon), hasCalculation(container)),
    ]),
    group("dispatch", "Dispatch", [
      action("DISPATCH_REQUEST", "Request Dispatch", icon(ChatBubbleLeftEllipsisIcon), edit("DISPATCH_REQUEST")),
      action("DISPATCH", "New Dispatch", icon(PlusIcon), createDispatch && offered.has("DISPATCH")),
    ]),
    group("container", "Container", [
      action("TOGGLE_DO_NOT_DISPATCH", container.doNotDispatch ? "Mark Dispatchable" : "Mark Do Not Dispatch",
        icon(TruckIcon), edit("TOGGLE_DO_NOT_DISPATCH")),
      action("TOGGLE_DNU", container.doNotUse ? "Mark Usable" : "Mark Do Not Use", icon(BeakerIcon), edit("TOGGLE_DNU")),
      action("TOGGLE_DISPATCH_IGNORE_MAX_NON_PRIMARY_VOLUME",
        container.dispatchIgnoreMaxNonPrimaryVolume ? "Stop Ignoring Max Non-Primary Volume" : "Ignore Max Non-Primary Volume",
        icon(BeakerIcon), edit("TOGGLE_DISPATCH_IGNORE_MAX_NON_PRIMARY_VOLUME")),
      action("TOGGLE_LACT_METER_DNU", lact?.doNotUse ? "Enable LACT Meter" : "Disable LACT Meter", icon(BeakerIcon),
        !!lact && edit("TOGGLE_LACT_METER_DNU")),
      action("TOGGLE_REJECTION", hasRejection(container) ? "Clear Rejection" : "Set Rejection", icon(BeakerIcon),
        edit("TOGGLE_REJECTION")),
    ]),
    group("site", "Site", [
      action("TOGGLE_SITE_TRANSPORT_VIA_PIPELINE",
        container.siteTransportViaPipeline ? "Transport Water by Truck" : "Transport Water by Pipeline", icon(TruckIcon),
        edit("TOGGLE_SITE_TRANSPORT_VIA_PIPELINE") && !!container.siteId && container.siteTransportViaPipeline !== undefined),
      action("TOGGLE_SITE_TRANSPORT_OIL_VIA_PIPELINE",
        container.siteTransportOilViaPipeline ? "Transport Oil by Truck" : "Transport Oil by Pipeline", icon(TruckIcon),
        edit("TOGGLE_SITE_TRANSPORT_OIL_VIA_PIPELINE") && !!container.siteId && container.siteTransportOilViaPipeline !== undefined),
    ]),
    group("tags", "Tags", [action("MANAGE_TAGS", "Manage Tags", icon(TagIcon), edit("MANAGE_TAGS"))]),
  ].filter((item) => item.actions.length);
};
